// Глобальная справка «Как это работает» — постоянный вход в объяснение продукта
// (PRODUCT-NARRATIVE §7). Открывается кнопкой на дашборде, пунктом меню профиля
// и (позже) баннерами разделов, поэтому состояние живёт в сторе, а не в
// поверхности: иначе каждая точка входа завела бы свою копию модалки.
// Монтируется один раз в __root.tsx (<HowItWorksDialog />), как login-modal /
// upgrade-modal. См. rules/frontend-state.md §5.4.
//
// Стор чистый (без сети и без аналитики) — событие `how_it_works_opened` шлёт
// сама модалка на переходе «закрыта → открыта», один раз на открытие и в одном
// месте. Трекинг на каждом вызывателе дал бы столько же копий правила, сколько
// точек входа.
import { create } from "zustand";

/**
 * Вкладка справки. ЕДИНСТВЕННОЕ объявление на приложение: и модалка, и любая
 * точка входа в неё (Главная, меню профиля) говорят об одних и тех же вкладках.
 *
 * Баннеры разделов в этот список больше не входят: вход «Как это работает» с
 * каждой страницы убран — со всех разделов он открывал одну и ту же справку
 * (решение владельца 2026-08-27), поэтому поля `intro.howItWorksTab` в
 * манифестах нет.
 */
export type HowItWorksTab = "method" | "course" | "tools";

/** Порядок вкладок в модалке. */
export const HOW_IT_WORKS_TABS = ["method", "course", "tools"] as const satisfies readonly [
	HowItWorksTab,
	...HowItWorksTab[],
];

export const DEFAULT_HOW_IT_WORKS_TAB: HowItWorksTab = "method";

/**
 * Откуда позвали справку — уходит в аналитику как есть (`source`).
 * Свободная строка, а не enum: точки входа заводятся разными поверхностями
 * (баннеры разделов, пустые состояния), и общий список пришлось бы править
 * при каждой новой — ровно то, от чего уходит модульная платформа.
 */
export type HowItWorksSource = string;

export interface HowItWorksOpenOptions {
	/** Вкладка, на которой открыть. Не задана — «Метод». */
	tab?: HowItWorksTab;
	/** Точка входа для аналитики. */
	source?: HowItWorksSource;
}

interface HowItWorksState {
	isOpen: boolean;
	tab: HowItWorksTab;
	source: HowItWorksSource;
	open: (options?: HowItWorksOpenOptions) => void;
	/** Переключение вкладки внутри открытой модалки. */
	setTab: (tab: HowItWorksTab) => void;
	close: () => void;
	setOpen: (open: boolean) => void;
}

export const useHowItWorksStore = create<HowItWorksState>((set) => ({
	isOpen: false,
	tab: DEFAULT_HOW_IT_WORKS_TAB,
	source: "unknown",
	open: (options) =>
		set({
			isOpen: true,
			tab: options?.tab ?? DEFAULT_HOW_IT_WORKS_TAB,
			source: options?.source ?? "unknown",
		}),
	setTab: (tab) => set({ tab }),
	close: () => set({ isOpen: false }),
	setOpen: (isOpen) => set(isOpen ? { isOpen } : { isOpen: false }),
}));
